import { useEffect, useState, type PropsWithChildren, type ReactNode } from "react";

/**
 * The slice of an oidc-client-ts UserManager this needs. Kept structural so
 * frontend-common stays free of an oidc-client-ts / react-oidc-context
 * dependency — the same reason createAuthErrorLink takes callbacks. Neptune's
 * remote build stubs react-oidc-context entirely and would break on a real
 * import.
 */
export interface RestorableSession {
  getUser: () => Promise<{
    expired?: boolean;
    refresh_token?: string;
  } | null>;
  signinSilent: () => Promise<unknown>;
}

export interface SilentSessionRestoreOptions {
  /**
   * Skip the restore entirely. Pass true while the signin callback owns the
   * userManager — a competing renew races the code exchange.
   */
  skip?: boolean;
}

/**
 * Restores an expired session from the stored refresh token before the UI is
 * allowed to conclude the user is signed out. Returns false until the attempt
 * has settled; hold the "logged out" branch of the render until it is true.
 *
 * react-oidc-context reports `isAuthenticated: !user.expired`, so on any load
 * more than `access.token.lifespan` (1h) after the last activity every app
 * treats the session as gone before automaticSilentRenew gets a turn. Venus
 * redirected to Keycloak, Neptune showed its login screen; either way the
 * offline refresh token — good for 30 days per the realm's
 * offlineSessionIdleTimeout — went unused, and the session survived only as
 * long as the Keycloak SSO cookie, which without remember-me does not outlive
 * a browser restart.
 *
 * Only a token the server actually rejects should reach the signed-out UI.
 */
export function useSilentSessionRestore(
  session: RestorableSession,
  options: SilentSessionRestoreOptions = {},
): boolean {
  const { skip = false } = options;
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        if (skip) return;

        const stored = await session.getUser();
        if (stored?.expired && stored.refresh_token) {
          await session.signinSilent();
        }
      } catch (error) {
        // Refresh token rejected (revoked, or past the 30d offline idle
        // timeout). Fall through to the caller's signed-out branch.
        console.warn("Silent session restore failed, falling back to login:", error);
      } finally {
        if (!cancelled) setSettled(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [session, skip]);

  return settled;
}

interface SilentSessionRestoreProps
  extends PropsWithChildren,
    SilentSessionRestoreOptions {
  session: RestorableSession;
  fallback: ReactNode;
}

/**
 * Component form of {@link useSilentSessionRestore} for apps that gate the
 * whole tree (Venus). Apps with their own signed-out branches (Neptune) should
 * use the hook and gate that branch instead.
 */
export function SilentSessionRestore({
  session,
  fallback,
  skip,
  children,
}: SilentSessionRestoreProps) {
  const settled = useSilentSessionRestore(session, { skip });

  if (!settled) return <>{fallback}</>;

  return <>{children}</>;
}
